<template>
  <!-- 商机选择：单选，弹框里挑一条商机。用法与 CustomerSelect 一致 -->
  <div class="opportunity-select">
    <div class="name el-select__prefix" @click="toShowDialog">
      <slot name="prefix"></slot>
    </div>
    <!-- 回显区：已选商机名称，可单独清空 -->
    <div class="value" @click="toShowDialog">
      <div v-if="selectedItems.length" class="select-wrap">
        <div class="select-tag">
          <span class="tag-name">{{ selectedItems[0].name }}</span>
          <yq-icon v-if="!disabled" class="tag-close" name="element-ui_close" @click.stop="clearData"/>
        </div>
      </div>
      <span v-else class="placeholder">{{ placeholder }}</span>
    </div>

    <el-dialog v-model="showDialog" title="选择商机" dialog-width="3" :close-on-click-modal="false" append-to-body>
      <dialog-table>
        <list-page :show-shadow="false" :show-border="false">
          <search-condition :show-shadow="false" :show-border="false">
            <template #default>
              <el-input v-model="dialogSearchInfo.searchKey" placeholder="请输入商机名称/编号" @keydown.enter="resetSearchDialog">
                <template #prefix>关键字</template>
              </el-input>
            </template>
            <template #option>
              <page-button type="read" el-type="primary" @click="resetSearchDialog">查询</page-button>
            </template>
          </search-condition>
          <table-list>
            <yq-table :data="dialogList" border>
              <yq-table-column label="商机名称" prop="opportunityTitle" min-width="240rem" :show-overflow-tooltip="true"/>
              <yq-table-column label="商机编号" prop="opportunityNo" width="120rem"/>
              <yq-table-column label="当前阶段" prop="currentStage" width="120rem" align="center">
                <template v-slot="{row}">
                  <el-tag size="small" :type="stageTagType(row.currentStage)">{{ stageName(row.currentStage) }}</el-tag>
                </template>
              </yq-table-column>
              <yq-table-column label="商机金额" prop="opportunityAmount" width="120rem" align="right">
                <template v-slot="{row}">{{ money(row.opportunityAmount) }}</template>
              </yq-table-column>
              <yq-table-column label="负责人" prop="ownerEmployeeNames" min-width="120rem"/>
              <yq-table-column label="操作" prop="options" align="center" width="60rem" fixed="right">
                <template v-slot="{row}">
                  <table-button @click="selectOpportunity(row)">选择</table-button>
                </template>
              </yq-table-column>
            </yq-table>
          </table-list>
          <form-page :show-shadow="false" :show-border="false" v-model:page-number="dialogSearchInfo.pageNumber"
                     v-model:page-size="dialogSearchInfo.pageSize" :total-count="dialogSearchInfo.totalCount"
                     @onLoad="dialogQueryList">
            <page-button @click="showDialog = false">取消</page-button>
          </form-page>
        </list-page>
      </dialog-table>
    </el-dialog>
  </div>
</template>

<script>

import {watch} from "vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import TableList from "@/components/h5/layout/ListTable.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import DialogTable from "@/components/h5/layout/DialogTable.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import commonDataApi from "@/api/normal/common/commonDataApi";
import commonApi from "@/api/commonApi";
import YqIcon from "@/components/YqIcon.vue";
import {DICT_OPPORTUNITY_STAGE, dictName, stageTagType} from "@/views/h5/crm/crmDictionary";

// 选择一个商机，用于表单或查询条件。
// 值为商机档案id(partnerId，即 ba_business_partner.id)：跟进记录、日程都按这个 id 关联商机
export default {
  name: "OpportunitySelect",
  emits: ["change", "update:modelValue"],
  props: {
    modelValue: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: "请选择商机"
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  components: {
    YqIcon,
    TableButton, DialogTable, PageButton, TableList, FormPage, YqTableColumn, ListPage, SearchCondition
  },
  data() {
    return {
      showDialog: false,
      dialogSearchInfo: {},
      dialogList: [],
      stageList: [],
      selectedItems: [], // 回显的已选商机 [{id: partnerId, name: opportunityTitle}]
      isSelectData: false, // 是在前端选择的数据
    };
  },
  created() {
    // 阶段是字典值，后端只返回 currentStage，名称由前端关联出来
    commonApi.queryUniqueDictionaryList(DICT_OPPORTUNITY_STAGE).then(list => this.stageList = list || []);
    this.init();
    watch(() => this.modelValue, () => {
      this.init()
    });
  },
  methods: {
    init() {
      if (!this.modelValue) {
        this.selectedItems = [];
        return;
      }
      if (this.isSelectData) {// 是在前端选择的数据，名称已经有了，不用再查
        return;
      }
      // 按 id 查名称回显。走的是往来单位通用接口：商机与客户同表，
      // 按 partner_id 查回来的 merchant_remark_name 就是商机名称
      const ids = [this.modelValue];
      const idsKey = this.modelValue;
      commonDataApi.queryPartnerBaseInfoByIds(ids).then(res => {
        // 响应回来时可能已经换成别的商机了。用过期响应覆盖会把当前的回显换掉，所以对不上就丢弃
        if (this.modelValue !== idsKey) {
          return;
        }
        this.selectedItems = (res || []).map(r => ({id: r.partnerId, name: r.merchantRemarkName}));
      }).catch(() => {});
    },
    toShowDialog() {
      if (this.disabled) {
        return;
      }
      this.showDialog = true;
      if (!this.dialogList.length) {
        this.resetSearchDialog();
      }
    },
    resetSearchDialog() {
      this.dialogSearchInfo.pageNumber = 1;
      this.dialogQueryList();
    },
    dialogQueryList() {
      // 走通用接口（商机下拉专用），数据权限与商机列表页一致
      commonDataApi.queryOpportunityList(this.dialogSearchInfo).then(res => {
        this.dialogList = res.records;
        this.dialogSearchInfo.totalCount = res.totalCount;
      })
    },
    selectOpportunity(data) {
      // 建档口径同 CustomerSelect：存档案id，不存商机行主键
      this.selectedItems = [{id: data.partnerId, name: data.opportunityTitle}];
      this.selectDataFront();
      this.$emit("update:modelValue", data.partnerId);
      this.$emit("change", data.partnerId);
      this.showDialog = false;
    },
    // 前端选中了某个数据
    selectDataFront() {
      this.isSelectData = true;
      setTimeout(() => this.isSelectData = false, 300);
    },
    clearData() {
      this.selectedItems = [];
      this.selectDataFront();
      this.$emit("update:modelValue", "");
      this.$emit("change", "");
    },
    stageName(stageValue) {
      return dictName(this.stageList, stageValue);
    },
    stageTagType(stageValue) {
      return stageTagType(stageValue);
    },
    money(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      return "￥" + Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    }
  }
};
</script>

<style lang="scss" scoped>
.opportunity-select{
  display: inline-flex !important;
  border: none !important;
  align-items: center;
  padding: 0 10px;
  min-width: 240px;
  box-sizing: border-box;
  border-radius: var(--el-border-radius-base);
  box-shadow: 0 0 0 0.02667rem var(--el-border-color) inset;
  min-height: 34px;
  font-size: 14px;
  position: relative;
  .name{
    font-size: 14px;
    color: var(--yq-font-color-base);
    gap: 0;
    &:not(:empty):after  {
      content: ":";
      margin-right: 8px;
    }
  }
  .value{
    flex: 1;
    color: #333;
    .placeholder{
      color: #999;
    }
  }
  .select-wrap{
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 2px 0;
    .select-tag{
      display: inline-flex;
      align-items: center;
      padding: 0 8px;
      height: 22px;
      margin: 2px;
      background: var(--el-color-primary-light-9, #ecf5ff);
      border: 1px solid var(--el-color-primary-light-5, #b3d8ff);
      border-radius: 4px;
      font-size: 12px;
      color: var(--el-color-primary);
      .tag-close{
        margin-left: 4px;
        cursor: pointer;
        font-size: 12px;
        &:hover{
          color: var(--el-color-danger);
        }
      }
    }
  }
}
</style>
